import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from "../provider/authProvider";
import { login } from '../api/api';
import { ArrowLeft, Eye, EyeOff, Lock, Mail, UserPlus, LogIn } from 'lucide-react';

const Logo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7.5L12 4L20 7.5L12 11L4 7.5Z" fill="currentColor" />
    <path d="M4 11.5L12 15L20 11.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 15.5L12 19L20 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { token, setToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await login(email, password);

      if (response?.token) {
        setToken(response.token);
        navigate("/dashboard");
      } else {
        setToken("");
        setError("Erro durante o login, tente novamente.");
      }
    } catch (error) {
      setToken("");
      const errorMessage = error?.response?.data?.message || error?.message || '';
      if (errorMessage.toLowerCase().includes('ativ') || errorMessage.toLowerCase().includes('activate') || error?.response?.status === 403) {
        setError("Sua conta ainda não foi ativada. Verifique seu email ou solicite um novo link de ativação.");
      } else {
        setError("Erro durante o login, tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center p-4 bg-grid">
      <div className="absolute top-8 left-8">
        <Link 
          to="/"
          className="flex items-center gap-2 text-gray-500 hover:text-purple-600 font-bold transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center group-hover:bg-purple-50 group-hover:border-purple-100 shadow-sm transition-all">
            <ArrowLeft size={18} />
          </div>
          <span>Voltar ao início</span>
        </Link>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="flex items-center text-black mb-4">
            <Logo size={48} />
            <span className="font-display text-4xl ml-4 tracking-tighter">SORA</span>
          </div>
          <h1 className="text-3xl font-black mb-2 font-hero">
            Boas-vindas de volta!
          </h1>
          <p className="text-gray-500 font-medium">
            Acesse sua conta para gerenciar suas aulas.
          </p>
        </div>

        <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl border border-gray-100">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
              <p className="text-red-800 font-medium text-sm">{error}</p>
              {error.includes('ativada') && (
                <div className="mt-2">
                  <Link to="/account/activate" className="text-red-600 hover:text-red-700 font-bold text-sm underline">
                    Solicitar novo link de ativação
                  </Link>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Seu e-mail
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Mail size={18} />
                </div>
                <input 
                  type="email" 
                  placeholder="exemplo@escola.com" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <label className="block text-sm font-black uppercase tracking-widest text-gray-400 ml-1">
                  Sua senha
                </label>
                <Link to="/password/forgot" className="text-xs font-bold text-purple-600 hover:text-purple-700">
                  Esqueceu a senha?
                </Link>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Lock size={18} />
                </div>
                <input 
                  type={showPassword ? "text" : "password"} 
                  placeholder="••••••••" 
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-12 outline-none transition-all font-medium"
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit"
              disabled={loading}
              className={`w-full bg-black text-white font-display py-5 rounded-2xl text-lg shadow-xl shadow-gray-200 transition-all active:scale-95 flex items-center justify-center gap-3 ${loading ? 'opacity-70 cursor-wait' : 'hover:bg-gray-800'}`}
            >
              {loading ? (
                <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <LogIn size={20} />
                  Entrar na plataforma
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-50 text-center">
            <p className="text-gray-500 font-medium">
              Ainda não tem conta? <br className="sm:hidden" />
              <Link 
                to="/account/create" 
                className="text-purple-600 font-bold hover:underline ml-1"
              >
                Crie uma conta
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      <p className="mt-12 text-gray-400 text-sm font-medium">
        © 2025 Sôra — Todos os direitos reservados.
      </p>
    </div>
  );
};

export default Login;
